import React from 'react';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { AuthContext } from 'auth';
import { areFeatureFlagsEnabled, getJobsiteProviderConfig } from './config';
import { PassThroughProvider } from './passThroughProvider';

type LDProvider = React.FunctionComponent<{ children: React.ReactNode }>;

/**
 * Initializes a jobsite-scoped LaunchDarkly provider (its own client) for the
 * given jobsite. Mirrors {@link useLDProvider} but keys the LD context on the
 * jobsite (plus the user, when present), so jobsite-targeted flags can be read
 * via `useFlags()` even for unauthenticated users.
 *
 * Returns `undefined` while initializing (or when feature flags are disabled);
 * callers should treat that as "not ready".
 */
export const useJobsiteLDProvider = (
  jobsiteId: string,
): LDProvider | undefined => {
  const { currentUser: user } = React.useContext(AuthContext);
  const [ldProvider, setLDProvider] = React.useState<LDProvider>();

  React.useEffect(() => {
    if (jobsiteId && areFeatureFlagsEnabled()) {
      // guard against component unmount during asyncWithLDProvider resolution
      let isMounted = true;
      const providerConfig = getJobsiteProviderConfig(jobsiteId, user);
      asyncWithLDProvider(providerConfig)
        .then((component) => {
          if (isMounted) {
            setLDProvider(() => component);
          }
        })
        .catch(() => {
          // Degrade to the user-context provider
          if (isMounted) {
            setLDProvider(() => PassThroughProvider);
          }
        });
      return (): void => {
        isMounted = false;
      };
    }
    return undefined;
  }, [jobsiteId, user?.userAccountId]);

  return ldProvider;
};
